import { useCallback, useEffect, useState } from 'react';
import { Icon } from '@/components/ui/Icon';
import { StatusBadge } from '@/components/ui';
import { adminApi, type CampaignDid, type CampaignDidBank as Bank } from '@/shared/api/adminApi';
import { toast } from '@/components/ui/Toast';

/** Ordered burned badge shown on any burned number. */
function BurnedBadge({ reason }: { reason?: string | null }) {
  return (
    <span title={reason ?? 'המספר סומן כשרוף'} style={{ display: 'inline-flex' }}>
      <StatusBadge tone="danger" size="sm" dot>שרוף</StatusBadge>
    </span>
  );
}

/**
 * Ordered caller-id DID bank for a campaign. The order IS the rotation order;
 * add from the available pool, remove, and reorder up/down. Burned numbers show
 * a red badge and cannot be added to the bank.
 */
export function CampaignDidBank({ campaignId }: { campaignId: number }) {
  const [bank, setBank] = useState<CampaignDid[]>([]);
  const [available, setAvailable] = useState<CampaignDid[]>([]);
  const [rotation, setRotation] = useState<Bank['rotation']>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const apply = useCallback((d: Bank) => {
    setBank([...d.bank].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));
    setAvailable(d.available);
    setRotation(d.rotation ?? {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    void adminApi.campaignDids(campaignId)
      .then(apply)
      .catch(() => toast('שגיאה בטעינת בנק המספרים', 'danger'))
      .finally(() => setLoading(false));
  }, [campaignId, apply]);

  useEffect(() => { load(); }, [load]);

  async function persist(ids: number[]) {
    setSaving(true);
    try {
      apply(await adminApi.saveCampaignDids(campaignId, ids));
      toast('בנק המספרים עודכן', 'ok');
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast(msg ?? 'שגיאה בשמירת בנק המספרים', 'danger');
      load();
    } finally {
      setSaving(false);
    }
  }

  const bankIds = bank.map((d) => d.id);
  const addDid = (d: CampaignDid) => { if (!d.burned) void persist([...bankIds, d.id]); };
  const removeDid = (id: number) => void persist(bankIds.filter((x) => x !== id));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= bankIds.length) return;
    const next = [...bankIds];
    [next[i], next[j]] = [next[j], next[i]];
    void persist(next);
  };

  if (loading) return <div style={{ color: 'var(--muted)' }}>טוען…</div>;

  return (
    <div className="stack">
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <strong style={{ fontSize: 'var(--t-sm)' }}>בנק מספרים מזוהים ({bank.length})</strong>
          {rotation.current_did_id != null && (
            <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
              מספר נוכחי: <span dir="ltr" style={{ fontFamily: 'var(--font-mono)' }}>
                {bank.find((d) => d.id === rotation.current_did_id)?.number ?? `#${rotation.current_did_id}`}
              </span>
              {rotation.unanswered != null && rotation.unanswered > 0 && ` · ללא מענה מאז החלפה: ${rotation.unanswered}`}
            </span>
          )}
        </div>
        {bank.length === 0 ? (
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
            אין מספרים בבנק — יתבצע חיוג עם המספר המזוהה של הקמפיין/העסק. הוסף מספרים כדי לאפשר רוטציה.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 4 }}>
            {bank.map((d, i) => (
              <div
                key={d.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                  background: d.id === rotation.current_did_id ? 'var(--surface-2)' : 'var(--surface)',
                  border: '1px solid var(--line)', borderRadius: 'var(--r-2)',
                }}
              >
                <span style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)', width: 20, textAlign: 'center' }}>{i + 1}</span>
                <span dir="ltr" style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sm)' }}>{d.number}</span>
                {d.burned && <BurnedBadge reason={d.burned_reason} />}
                <button type="button" className="btn ghost" aria-label="הזז למעלה" disabled={saving || i === 0} onClick={() => move(i, -1)}><Icon name="chevronD" size={14} style={{ transform: 'rotate(180deg)' }} /></button>
                <button type="button" className="btn ghost" aria-label="הזז למטה" disabled={saving || i === bank.length - 1} onClick={() => move(i, 1)}><Icon name="chevronD" size={14} /></button>
                <button type="button" className="btn danger" aria-label="הסר מהבנק" disabled={saving} onClick={() => removeDid(d.id)}><Icon name="trash" size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <strong style={{ fontSize: 'var(--t-sm)', display: 'block', marginBottom: 6 }}>מספרים זמינים להוספה</strong>
        {available.length === 0 ? (
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>אין מספרים נוספים זמינים לעסק.</div>
        ) : (
          <div style={{ display: 'grid', gap: 4 }}>
            {available.map((d) => (
              <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', border: '1px solid var(--line)', borderRadius: 'var(--r-2)' }}>
                <span dir="ltr" style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sm)' }}>{d.number}</span>
                {d.burned ? (
                  <BurnedBadge reason={d.burned_reason} />
                ) : (
                  <button type="button" className="btn" disabled={saving} onClick={() => addDid(d)}>הוסף</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
